/**
 * Header Cart Badge Updater
 * Dynamically updates the header cart badge count when SlideCartHQ cart changes
 * Filters out items with 'free-addon' tag to match Liquid template logic
 */
(function() {
  'use strict';

  // ===== Configuration Constants =====
  const FREE_ADDON_TAG = 'free-addon';
  const DEBOUNCE_DELAY = 150; // ms to debounce cart updates
  const CART_API_ENDPOINT = '/cart.js';

  // ===== State Management =====
  let updateTimeout = null;
  let isUpdating = false;

  // ===== DOM Element Cache =====
  let badgeElement = null;

  // ===== Product Tags Cache =====
  // Cache product tags to avoid repeated API calls
  const productTagsCache = new Map();

  /**
   * Initialize DOM cache
   * Finds and caches the header badge element
   * @private
   */
  function initializeDOMCache() {
    if (!badgeElement) {
      badgeElement = document.querySelector('.header-badge[data-cart-count]');
    }
    return badgeElement;
  }

  /**
   * Debounce function to limit cart update calls
   * Prevents excessive API requests during rapid cart changes
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function} - Debounced function
   * @private
   */
  function debounce(func, wait) {
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(updateTimeout);
        updateTimeout = null;
        func(...args);
      };
      clearTimeout(updateTimeout);
      updateTimeout = setTimeout(later, wait);
    };
  }

  /**
   * Check if a product has the 'free-addon' tag
   * Checks multiple possible locations for tag data
   * @param {Object} item - Cart item object
   * @returns {boolean|null} - True if item has 'free-addon' tag, false if it doesn't, null if unknown
   * @private
   */
  function hasFreeAddonTag(item) {
    // Method 1: Check if SlideCartHQ provides tags directly
    if (item.product_tags && Array.isArray(item.product_tags)) {
      return item.product_tags.includes(FREE_ADDON_TAG);
    }

    // Method 2: Check if product object has tags
    if (item.product && item.product.tags) {
      if (Array.isArray(item.product.tags)) {
        return item.product.tags.includes(FREE_ADDON_TAG);
      }
      if (typeof item.product.tags === 'string') {
        return item.product.tags.includes(FREE_ADDON_TAG);
      }
    }

    // Method 3: Check cached product tags
    if (item.product_id && productTagsCache.has(item.product_id)) {
      return productTagsCache.get(item.product_id);
    }

    // If no tag info available, return null to indicate unknown status
    return null;
  }

  /**
   * Fetch product tags for a product ID
   * Caches the result to avoid repeated API calls
   * @param {number} productId - Product ID
   * @returns {Promise<boolean>} - True if product has 'free-addon' tag
   * @private
   */
  async function fetchProductTags(productId) {
    // Check cache first
    if (productTagsCache.has(productId)) {
      return productTagsCache.get(productId);
    }

    try {
      // Fetch product data from Shopify
      const response = await fetch(`/products/${productId}.js`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        // If fetch fails, assume not a free-addon
        productTagsCache.set(productId, false);
        return false;
      }

      const product = await response.json();
      const hasTag = product.tags && 
        (Array.isArray(product.tags) ? product.tags.includes(FREE_ADDON_TAG) : 
         typeof product.tags === 'string' ? product.tags.includes(FREE_ADDON_TAG) : false);

      // Cache the result
      productTagsCache.set(productId, hasTag);
      return hasTag;
    } catch (error) {
      console.error(`[HeaderCartBadge] Error fetching product tags for ${productId}:`, error);
      // On error, assume not a free-addon
      productTagsCache.set(productId, false);
      return false;
    }
  }

  /**
   * Calculate filtered cart count
   * Excludes items with 'free-addon' tag to match Liquid template logic
   * @param {Array} items - Cart items array from Shopify
   * @returns {Promise<number>} - Filtered cart count
   * @private
   */
  async function calculateFilteredCartCount(items) {
    if (!items || !Array.isArray(items)) {
      return 0;
    }

    let filteredCount = 0;
    const itemsNeedingTagCheck = [];

    // First pass: count items where we can immediately determine tag status
    items.forEach((item) => {
      const hasTag = hasFreeAddonTag(item);
      
      if (hasTag === null && item.product_id) {
        // Tag status unknown, need to fetch
        itemsNeedingTagCheck.push(item);
      } else if (hasTag === false) {
        // Definitely not a free-addon, count it
        filteredCount += item.quantity || 0;
      }
      // If hasTag is true, skip counting (it's a free-addon)
    });

    // Second pass: fetch tags for items that need checking
    if (itemsNeedingTagCheck.length > 0) {
      const tagChecks = itemsNeedingTagCheck.map(async (item) => {
        const hasTag = await fetchProductTags(item.product_id);
        if (!hasTag) {
          return item.quantity || 0;
        }
        return 0;
      });

      const counts = await Promise.all(tagChecks);
      filteredCount += counts.reduce((sum, count) => sum + count, 0);
    }

    return filteredCount;
  }

  /**
   * Update the header badge with filtered cart count
   * @param {number} count - The filtered cart count to display
   * @private
   */
  function updateBadge(count) {
    const badge = initializeDOMCache();
    
    if (!badge) {
      console.warn('[HeaderCartBadge] Badge element not found');
      return;
    }

    // Update badge text content
    badge.textContent = count || '0';
  }

  /**
   * Fetch cart data from Shopify API
   * @returns {Promise<Object>} - Cart data object
   * @private
   */
  async function fetchCartData() {
    try {
      const response = await fetch(CART_API_ENDPOINT, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Cart API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('[HeaderCartBadge] Error fetching cart data:', error);
      throw error;
    }
  }

  /**
   * Process cart update
   * Fetches cart data, calculates filtered count, and updates badge
   * @private
   */
  async function processCartUpdate() {
    // Prevent concurrent updates
    if (isUpdating) {
      return;
    }

    isUpdating = true;

    try {
      const cartData = await fetchCartData();
      const filteredCount = await calculateFilteredCartCount(cartData.items);
      updateBadge(filteredCount);
    } catch (error) {
      // Error already logged in fetchCartData
      // Optionally update badge to show error state or keep current value
    } finally {
      isUpdating = false;
    }
  }

  /**
   * Debounced cart update handler
   * Prevents excessive API calls during rapid cart changes
   * @private
   */
  const debouncedCartUpdate = debounce(processCartUpdate, DEBOUNCE_DELAY);

  /**
   * Handle SlideCartHQ cart update callback
   * Official SlideCartHQ API callback that fires when cart is updated
   * @param {Object} cart - Cart data from SlideCartHQ (may be undefined)
   * @private
   */
  async function handleSlideCartUpdate(cart) {
    // If cart data is provided, use it directly
    if (cart && cart.items) {
      const filteredCount = await calculateFilteredCartCount(cart.items);
      updateBadge(filteredCount);
    } else {
      // Otherwise fetch fresh cart data
      debouncedCartUpdate();
    }
  }

  /**
   * Handle custom cart refresh event
   * Listens to custom events dispatched by other cart modules
   * @param {Event} event - Custom event object
   * @private
   */
  async function handleCartRefresh(event) {
    // If cart data is provided in event detail, use it
    if (event.detail && event.detail.cartData) {
      const filteredCount = await calculateFilteredCartCount(event.detail.cartData.items);
      updateBadge(filteredCount);
    } else {
      // Otherwise fetch fresh cart data
      debouncedCartUpdate();
    }
  }

  /**
   * Initialize the cart badge updater
   * Sets up event listeners and SlideCartHQ callback
   * @private
   */
  function initialize() {
    // Initialize DOM cache
    initializeDOMCache();

    // Set up SlideCartHQ official callback
    // This is the primary method for listening to cart changes
    if (typeof window.SLIDECART_UPDATED === 'undefined') {
      window.SLIDECART_UPDATED = handleSlideCartUpdate;
    } else {
      // If callback already exists, wrap it to preserve existing functionality
      const existingCallback = window.SLIDECART_UPDATED;
      window.SLIDECART_UPDATED = function(cart) {
        if (typeof existingCallback === 'function') {
          existingCallback(cart);
        }
        handleSlideCartUpdate(cart);
      };
    }

    // Listen to custom cart events as fallback
    // These events are dispatched by other cart modules (e.g., cart-oatmilk-handler.js)
    document.documentElement.addEventListener('cart:refresh', handleCartRefresh);
    document.addEventListener('cart:updated', handleCartRefresh);

    // Initial cart count update on page load
    // Wait for DOM to be ready and SlideCartHQ to be initialized
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(processCartUpdate, 500);
      });
    } else {
      setTimeout(processCartUpdate, 500);
    }
  }

  // ===== Initialization =====
  // Initialize when script loads
  initialize();
})();

